import React from "react";
import { Play, Search, Brain, Eye, Phone, Bell, Users, TrendingUp, Globe, Home, CheckCircle } from "lucide-react";

export default function LearnMore() {
  return (
    <div className="bg-gradient-to-br from-slate-50 to-white min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-800 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Ccircle cx=%2230%22 cy=%2230%22 r=%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Brain className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">UAE's First AI-Powered Platform</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Learn More About
              <span className="block bg-gradient-to-r from-cyan-300 to-teal-300 bg-clip-text text-transparent">
                JustOffPlan.ai
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-teal-100 max-w-3xl mx-auto mb-8 leading-relaxed">
              The revolutionary AI platform transforming off-plan property discovery in the UAE. 
              No middlemen. No commissions. Just intelligent connections.
            </p>
          </div>
        </div>
      </section>

      {/* Platform Video Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">See the Platform in Action</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Watch how our AI engine revolutionizes property discovery
          </p>
        </div>
        
        <div className="relative max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl aspect-video bg-gradient-to-br from-gray-900 to-gray-800">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-white/20 transition-colors">
                  <Play className="w-8 h-8 ml-1" />
                </div>
                <p className="text-lg">Platform Demo Video</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-700 max-w-3xl mx-auto text-lg leading-relaxed">
              Our intelligent AI engine cuts through the noise, filtering and ranking properties 
              based on your preferences, behavior, and budget. Experience a new era of property discovery.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How JustOffPlan.ai Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Five simple steps to finding your perfect off-plan property
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Search,
                title: "Smart Search",
                description: "Filter by budget, location, developer, handover year, and more with intelligent suggestions.",
                color: "bg-blue-500"
              },
              {
                icon: Brain,
                title: "AI Matchmaking",
                description: "Our engine analyzes your preferences and behavior to highlight the most relevant listings.",
                color: "bg-purple-500"
              },
              {
                icon: Eye,
                title: "Live Listings",
                description: "Access real-time brochures, payment plans, and availability synced directly with developers.",
                color: "bg-green-500"
              },
              {
                icon: Phone,
                title: "Direct Contact",
                description: "Connect directly with developers. Skip agents, avoid commissions, eliminate pressure.",
                color: "bg-orange-500"
              },
              {
                icon: Bell,
                title: "Smart Alerts",
                description: "Save your searches and receive instant notifications about matching new launches.",
                color: "bg-red-500"
              }
            ].map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-12 h-12 ${step.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center mb-4">
                    <span className="text-2xl font-bold text-gray-300 mr-3">{String(index + 1).padStart(2, '0')}</span>
                    <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unique Benefits */}
      <section className="py-20 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose JustOffPlan.ai?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Revolutionary features that set us apart from traditional property platforms
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              "UAE's first AI-native off-plan platform",
              "Built specifically for buyers and investors",
              "Zero agent interference, zero commission",
              "Live inventory with real-time data sync",
              "Complete transparency in every transaction",
              "Simplified, pressure-free experience"
            ].map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4 group">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-teal-600 group-hover:scale-110 transition-transform" />
                </div>
                <p className="text-gray-800 font-medium leading-relaxed">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Who Benefits from Our Platform?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Designed for modern property seekers across different profiles and needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: "First-time Buyers",
                description: "Compare properties easily without pressure or hidden commissions.",
                gradient: "from-blue-500 to-purple-600"
              },
              {
                icon: TrendingUp,
                title: "Global Investors",
                description: "Access Dubai's market remotely with comprehensive data and trust.",
                gradient: "from-green-500 to-teal-600"
              },
              {
                icon: Globe,
                title: "UAE Expats",
                description: "Discover community-focused projects that align with your lifestyle goals.",
                gradient: "from-orange-500 to-red-600"
              },
              {
                icon: Home,
                title: "Developers",
                description: "Connect directly with high-intent buyers without intermediaries.",
                gradient: "from-purple-500 to-pink-600"
              }
            ].map((audience, index) => (
              <div key={index} className="text-center group">
                <div className={`w-16 h-16 bg-gradient-to-r ${audience.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <audience.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{audience.title}</h3>
                <p className="text-gray-600 leading-relaxed">{audience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 md:px-8 lg:px-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Find Your Ideal Property?</h2>
          <p className="text-xl text-teal-100 mb-8 leading-relaxed max-w-2xl mx-auto">
            Start exploring AI-powered off-plan listings today. No agents. No fees. 
            Only real options, for real buyers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/exclusive"
              className="inline-flex items-center justify-center bg-white text-teal-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Browse Properties
              <Search className="w-5 h-5 ml-2" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-teal-700 transition-colors"
            >
              Get in Touch
              <Phone className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}